import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { STORAGE_KEYS } from '@/utils/storageKeys'
import {
  dismissSyncNotification,
  dismissSyncNotificationsFromList,
  isSyncNotificationDismissed,
  persistLastId,
  readPersistedLastId
} from '@/utils/adminNotificationCursor'

export const useNotificationStore = defineStore('notification', () => {
  // ========== STATE ==========
  const notifications = ref([])
  const showDropdown = ref(false)
  
  // ========== COMPUTED ==========
  
  /**
   * Contagem de notificações não lidas
   */
  const unreadCount = computed(() => {
    return notifications.value.filter(n => !n.read).length
  })
  
  /**
   * Notificações ordenadas por data (mais recentes primeiro)
   */
  const sortedNotifications = computed(() => {
    return [...notifications.value].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    )
  })
  
  // ========== ACTIONS ==========
  
  /**
   * Adiciona nova notificação
   * @param {object} options - { title, message, type, data }
   */
  function add(options) {
    const {
      title = 'Notificação',
      message = '',
      type = 'info', // 'success', 'error', 'warning', 'info'
      data = null,
      sseEventId = null
    } = options

    if (data && isSyncNotificationDismissed(data)) {
      return null
    }

    const syncJobId =
      data && typeof data === 'object' && data.kind === 'server_sync'
        ? String(data.job_id ?? '')
        : ''
    if (syncJobId !== '') {
      const duplicate = notifications.value.some(
        (n) => n.data?.kind === 'server_sync' && String(n.data?.job_id ?? '') === syncJobId
      )
      if (duplicate) {
        return null
      }
    }

    const provisionJobId =
      data && typeof data === 'object' && data.kind === 'account_provision'
        ? String(data.job_id ?? '')
        : ''
    if (provisionJobId !== '') {
      const duplicate = notifications.value.some(
        (n) =>
          n.data?.kind === 'account_provision' &&
          String(n.data?.job_id ?? '') === provisionJobId
      )
      if (duplicate) {
        return null
      }
    }
    
    const notification = {
      id: Date.now() + Math.random(), // ID único
      title,
      message,
      type,
      data,
      sseEventId: typeof sseEventId === 'number' ? sseEventId : null,
      timestamp: new Date().toISOString(),
      read: false
    }
    
    // Adicionar no início
    notifications.value.unshift(notification)
    
    // Manter apenas últimas 50 (evitar localStorage muito grande)
    if (notifications.value.length > 50) {
      notifications.value = notifications.value.slice(0, 50)
    }
    
    // Salvar no localStorage
    saveToStorage()
    
    return notification.id
  }
  
  /**
   * Marcar notificação como lida
   */
  function markAsRead(id) {
    const notif = notifications.value.find(n => n.id === id)
    if (notif) {
      notif.read = true
      if (notif.data) {
        dismissSyncNotification(notif.data)
      }
      if (typeof notif.sseEventId === 'number' && notif.sseEventId > 0) {
        persistLastId(Math.max(readPersistedLastId(), notif.sseEventId))
      }
      saveToStorage()
    }
  }
  
  /**
   * Marcar todas como lidas
   */
  function markAllAsRead() {
    dismissSyncNotificationsFromList(notifications.value)
    let maxSse = readPersistedLastId()
    notifications.value.forEach((n) => {
      n.read = true
      if (typeof n.sseEventId === 'number' && n.sseEventId > 0) {
        maxSse = Math.max(maxSse, n.sseEventId)
      }
    })
    if (maxSse > 0) {
      persistLastId(maxSse)
    }
    saveToStorage()
  }
  
  /**
   * Remover notificação específica
   */
  function remove(id) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index !== -1) {
      const [removed] = notifications.value.splice(index, 1)
      if (removed?.data) {
        dismissSyncNotification(removed.data)
      }
      if (typeof removed?.sseEventId === 'number' && removed.sseEventId > 0) {
        persistLastId(Math.max(readPersistedLastId(), removed.sseEventId))
      }
      saveToStorage()
    }
  }
  
  /**
   * Limpar todas as notificações
   */
  function clear() {
    dismissSyncNotificationsFromList(notifications.value)
    let maxSse = readPersistedLastId()
    for (const n of notifications.value) {
      if (typeof n.sseEventId === 'number' && n.sseEventId > 0) {
        maxSse = Math.max(maxSse, n.sseEventId)
      }
    }
    if (maxSse > 0) {
      persistLastId(maxSse)
    }
    notifications.value = []
    saveToStorage()
  }
  
  /**
   * Limpar notificações antigas (> 7 dias)
   */
  function cleanOld() {
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    const before = notifications.value.length
    
    notifications.value = notifications.value.filter(n => 
      new Date(n.timestamp) > sevenDaysAgo
    )
    
    if (before !== notifications.value.length) {
      saveToStorage()
    }
  }
  
  /**
   * Toggle dropdown
   */
  function toggleDropdown() {
    showDropdown.value = !showDropdown.value
  }
  
  /**
   * Fechar dropdown
   */
  function closeDropdown() {
    showDropdown.value = false
  }
  
  /**
   * Salvar no localStorage
   */
  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(notifications.value))
    } catch (error) {
      console.error('Erro ao salvar notificações:', error)
    }
  }
  
  /**
   * Carregar do localStorage
   */
  function dedupePersistentNotifications(list) {
    const seenSyncJobs = new Set()
    const seenProvisionJobs = new Set()
    return list.filter((n) => {
      if (n?.data?.kind === 'server_sync') {
        const jobId = String(n.data?.job_id ?? '')
        if (jobId === '') {
          return true
        }
        if (seenSyncJobs.has(jobId)) {
          return false
        }
        seenSyncJobs.add(jobId)
        return true
      }
      if (n?.data?.kind === 'account_provision') {
        const jobId = String(n.data?.job_id ?? '')
        if (jobId === '') {
          return true
        }
        if (seenProvisionJobs.has(jobId)) {
          return false
        }
        seenProvisionJobs.add(jobId)
        return true
      }
      return true
    })
  }

  function loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS)
      if (stored) {
        const parsed = JSON.parse(stored)
        notifications.value = dedupePersistentNotifications(Array.isArray(parsed) ? parsed : [])
          .filter((n) => !n?.data || !isSyncNotificationDismissed(n.data))
        cleanOld()
        saveToStorage()
        const maxSse = notifications.value.reduce(
          (max, n) => Math.max(max, typeof n.sseEventId === 'number' ? n.sseEventId : 0),
          readPersistedLastId()
        )
        if (maxSse > 0) {
          persistLastId(maxSse)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error)
      notifications.value = []
    }
  }
  
  /**
   * Formatar timestamp relativo (ex: "Há 5 minutos")
   */
  function getRelativeTime(timestamp) {
    const now = new Date()
    const date = new Date(timestamp)
    const seconds = Math.floor((now - date) / 1000)
    
    if (seconds < 60) return 'Agora'
    if (seconds < 3600) return `Há ${Math.floor(seconds / 60)} min`
    if (seconds < 86400) return `Há ${Math.floor(seconds / 3600)} h`
    if (seconds < 604800) return `Há ${Math.floor(seconds / 86400)} dias`
    
    // Mais de 7 dias: mostrar data
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }
  
  // ========== INIT ==========
  
  // Carregar ao inicializar
  loadFromStorage()
  
  // Limpar antigas periodicamente (a cada 1h)
  setInterval(cleanOld, 60 * 60 * 1000)
  
  return {
    // State
    notifications,
    showDropdown,
    
    // Computed
    unreadCount,
    sortedNotifications,
    
    // Actions
    add,
    markAsRead,
    markAllAsRead,
    remove,
    clear,
    cleanOld,
    toggleDropdown,
    closeDropdown,
    getRelativeTime
  }
})
